import React, { Fragment, useEffect, useRef, useState } from 'react';

import { Tab } from '@headlessui/react';
import cs from 'classnames';

import Button from 'components/shared/Buttons/Button';
import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Modal from 'components/shared/Modal/Modal';

import { Patients } from './Tabs';
import SelectedFiles from './Tabs/SelectedFiles';
import useSelectFiles from './lib/useSelectFiles';

const DocsModal = ({
  docsModalVisible,
  setDocsModalVisible,
  handleInsertFiles,
  documents,
  loading,
  setLoading
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const inputFieldRef = useRef(null);
  const [filters, setFilters] = useState({
    patientSearchTerm: '',
    docSearchTerm: '',
    patientsPage: 1,
    docsPage: 1,
    limit: 25
  });

  const handleClearSearchTerm = () => {
    setTabState((prev) => ({
      ...prev,
      [tabState.selectedPatient ? 'patientDocuments' : 'allPatients']: []
    }));
    setSearchTerm('');
    setFilters({
      ...filters,
      docSearchTerm: '',
      patientSearchTerm: '',
      docsPage: 1,
      patientsPage: 1
    });
  };

  const [tabState, setTabState] = useState({
    selectedPatient: null,
    selectedFiles: [],
    allPatients: [],
    patientDocuments: []
  });

  useEffect(() => {
    if (docsModalVisible) {
      inputFieldRef.current?.focus();
    }
  }, [docsModalVisible]);

  const forwardProps = { filters, setFilters, tabState, setTabState, setSearchTerm };

  const { selectFiles } = useSelectFiles({
    docsModalVisible,
    handleInsertFiles,
    documents,
    selectedFiles: tabState?.selectedFiles,
    setLoading
  });

  const tabsData = [
    {
      label: 'Patients',
      content: <Patients {...{ ...forwardProps }} />,
      icon: 'profile-2user',
      length: tabState.allPatients.length || 0
    },
    {
      label: 'Selected',
      content: <SelectedFiles {...{ ...forwardProps }} />,
      icon: 'attach',
      length: tabState.selectedFiles.length
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      handleChangeSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleChangeSearchTerm = (searchText) => {
    if (
      (tabState.selectedPatient && searchText == filters.docSearchTerm) ||
      (!tabState.selectedPatient && searchText === filters.patientSearchTerm)
    )
      return;
    setTabState((prev) => ({
      ...prev,
      [tabState.selectedPatient ? 'patientDocuments' : 'allPatients']: []
    }));
    setFilters({
      ...filters,
      [tabState.selectedPatient ? 'docSearchTerm' : 'patientSearchTerm']: searchText,
      [tabState.selectedPatient ? 'docsPage' : 'patientsPage']: 1
    });
  };

  return (
    <>
      <Modal
        handleOpen={!!docsModalVisible}
        handleClose={() => setDocsModalVisible(false)}
        title="Search from existing files"
        customStyling={{ width: '500px' }}
        className="bg-primary-10 !p-0 text-primary-900"
        slideFromRight
        footer={
          <div className="flex w-full justify-between">
            <Button
              outlined
              text="Cancel"
              color="neutral"
              onClick={() => setDocsModalVisible(false)}
              data-qa="cancel-select-files-btn"
            />
            <Button
              onClick={selectFiles}
              text="Insert files"
              id="createContactModalBtn"
              data-qa="select-files-btn"
              loadingIcon={loading}
              disabled={loading}
            />
          </div>
        }>
        <div>
          <div>
            <Input
              id="searchPatient"
              type="string"
              focusOnMount={true}
              forwardedRef={inputFieldRef}
              placeholder={
                tabState.selectedPatient
                  ? `Search ${tabState.selectedPatient.fullName} docs..`
                  : 'Search patient...'
              }
              value={searchTerm}
              icon="search-linear"
              onChange={(e) => setSearchTerm(e.target.value)}
              rightText={
                (filters.docSearchTerm || filters.patientSearchTerm) && (
                  <Icon icon="close-linear" onClick={handleClearSearchTerm} />
                )
              }
            />
          </div>
          <Tab.Group>
            <Tab.List className="mb-2 flex border-x-0 border-b border-t-0 border-solid border-neutral-200">
              {tabsData.map((tab, i) => {
                return (
                  <Tab as={Fragment} key={i} data-qa={tab.label} data-intercom-target={tab.label}>
                    {({ selected }) => (
                      <button
                        className={cs(
                          'flex cursor-pointer items-center gap-1 border-x-0 border-b-2 border-t-0 border-solid border-transparent !px-4 !py-2 text-sm leading-5',
                          selected
                            ? ' !border-primary-600 font-600 text-primary-500'
                            : 'font-500 text-neutral-800'
                        )}>
                        <Icon icon={tab.icon} />
                        {tab.label}
                        {tabState.selectedPatient && tab.label == 'Patients' && selected ? (
                          <div className="flex items-center">
                            <p className="font-500">-</p>
                            <h4 className="text-16 leading-24 font-s !px-1 text-sm font-600 text-primary-900">
                              {tabState.selectedPatient.f_name}
                            </h4>
                            <Icon
                              icon="close-square-mono"
                              size={20}
                              className="ml-1"
                              onClick={() => {
                                setTabState((prev) => ({ ...prev, selectedPatient: null }));
                                setFilters((prev) => ({ ...prev, patientSearchTerm: '' }));
                                setSearchTerm('');
                              }}
                            />
                          </div>
                        ) : (
                          tab.length > 0 && (
                            <label
                              className={cs(
                                '!ml-1 mb-0 grid !h-6 !w-6 cursor-pointer place-content-center rounded-full text-xs',
                                selected
                                  ? 'bg-primary-100 text-primary-500'
                                  : 'bg-neutral-100 text-neutral-700'
                              )}>
                              {tab.length}
                            </label>
                          )
                        )}
                      </button>
                    )}
                  </Tab>
                );
              })}
            </Tab.List>
            <Tab.Panels>
              {tabsData.map((tab, i) => (
                <Tab.Panel key={i}>
                  {({ selected }) => {
                    return selected ? tab.content : null;
                  }}
                </Tab.Panel>
              ))}
            </Tab.Panels>
          </Tab.Group>
        </div>
      </Modal>
    </>
  );
};

export default DocsModal;
